import { useEffect, useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Users, FileText, Award, ChevronLeft, Star, Sparkles, BookMarked, GraduationCap, Heart, Target, Lightbulb, Pen, Trophy, Globe, UserPlus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const extractYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&#?\s]+)/);
  return m ? m[1] : null;
};

const ICON_MAP = {
  BookOpen, Users, FileText, Award, Star, Sparkles, BookMarked, GraduationCap, Heart, Target, Lightbulb, Pen, Trophy, Globe,
};

const STAT_ICONS = { total_readers: Users, total_reports: FileText, total_courses: BookOpen, total_productions: Award };
const STAT_LABELS = { total_readers: 'قارئ وقارئة', total_reports: 'تقرير قرائي', total_courses: 'مساق تعليمي', total_productions: 'نتاج معتمد' };

const CountUp = ({ target }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start = 0;
    const step = Math.max(1, Math.floor(target / 66));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [target]);
  return <span>{count.toLocaleString('ar')}</span>;
};

// Smart Slider - grid if ≤3, infinite seamless CSS marquee if >3
const SmartSlider = ({ children, itemClass = '', slow = false }) => {
  const [isPaused, setIsPaused] = useState(false);
  const items = Array.isArray(children) ? children : [children];
  const count = items.length;
  const needsSlider = count > 3;

  if (!needsSlider) {
    return (
      <div className="flex justify-center gap-6 flex-wrap" data-testid="smart-slider-grid">
        {items.map((child, i) => (
          <div key={i} className={`w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] ${itemClass}`}>{child}</div>
        ))}
      </div>
    );
  }

  const duration = count * (slow ? 14 : 6);

  return (
    <div
      className="relative overflow-hidden"
      data-testid="smart-slider"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setTimeout(() => setIsPaused(false), 3000)}
      style={{
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
        maskImage: 'linear-gradient(to right, transparent, black 6%, black 94%, transparent)',
      }}
    >
      <style>{`
        @keyframes smartSliderMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .smart-slider-track { animation: none !important; }
        }
      `}</style>
      <div
        className="smart-slider-track flex will-change-transform"
        style={{
          width: 'max-content',
          animation: `smartSliderMarquee ${duration}s linear infinite`,
          animationPlayState: isPaused ? 'paused' : 'running',
        }}
      >
        {[0, 1].map(setIdx => (
          <div key={setIdx} className="flex gap-6 shrink-0 pl-6 items-center">
            {items.map((child, i) => (
              <div key={`${setIdx}-${i}`} className={`shrink-0 w-[280px] sm:w-[320px] lg:w-[350px] ${itemClass}`}>{child}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Fade-in on scroll using Intersection Observer
const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-[900ms] ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
};

// Testimonial showcase - 3 at a time, rising from bottom like messages
const TestimonialShowcase = ({ items, pc, ac }) => {
  const batchSize = 3;
  const totalBatches = Math.ceil(items.length / batchSize);
  const [batch, setBatch] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (totalBatches <= 1) return;
    const hold = setTimeout(() => setExiting(true), 12000);
    return () => clearTimeout(hold);
  }, [batch, totalBatches]);

  useEffect(() => {
    if (!exiting) return;
    const exit = setTimeout(() => {
      setBatch(p => (p + 1) % totalBatches);
      setExiting(false);
    }, 800);
    return () => clearTimeout(exit);
  }, [exiting, totalBatches]);

  const current = items.slice(batch * batchSize, (batch + 1) * batchSize);

  return (
    <>
      <style>{`
        @keyframes testimonialRise {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes testimonialExit {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-20px); }
        }
      `}</style>
      <div className="flex flex-wrap justify-center gap-6 items-start" data-testid="testimonial-showcase">
        {current.map((t, i) => (
          <div
            key={`${batch}-${i}`}
            className="bg-white p-6 rounded-2xl w-full md:w-[calc(33.333%-16px)]"
            style={{
              border: `1px solid ${pc}0a`,
              boxShadow: `0 4px 20px -4px ${ac}18, 0 8px 32px -8px ${ac}10`,
              animation: `${exiting ? 'testimonialExit' : 'testimonialRise'} 0.7s cubic-bezier(0.22,1,0.36,1) ${i * 150}ms both`,
            }}
          >
            <p className="font-bold leading-loose text-sm" style={{ color: ac }}>"{t.text}"</p>
            <div className="border-t pt-3 mt-4" style={{ borderColor: `${pc}15` }}>
              <p className="text-sm font-semibold" style={{ color: `${ac}99` }}>{t.name}</p>
              {t.role && <p className="text-xs" style={{ color: `${ac}70` }}>{t.role}</p>}
            </div>
          </div>
        ))}
      </div>
      {totalBatches > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalBatches }, (_, i) => (
            <div key={i} className="w-2 h-2 rounded-full transition-all duration-500"
              style={{ backgroundColor: i === batch ? pc : `${pc}30` }} />
          ))}
        </div>
      )}
    </>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [themeColors, setThemeColors] = useState({ primary: '#C19A6B', accent: '#5c3d2e' });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [siteSettings, setSiteSettings] = useState({ show_login_button: true, show_register_button: true });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/landing`).then(r => setSections(r.data)).catch(() => {}),
      axios.get(`${API}/public/stats`).then(r => setStats(r.data)).catch(() => {}),
      axios.get(`${API}/public/theme`).then(r => setThemeColors({ primary: r.data.primary || '#C19A6B', accent: r.data.accent || '#5c3d2e' })).catch(() => {}),
      axios.get(`${API}/registrations/site-settings`).then(r => setSiteSettings(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const hero = sections.hero || {};
  const about = sections.about || {};
  const features = sections.features || {};
  const testimonials = sections.testimonials || {};
  const videos = sections.videos || {};
  const gallery = sections.gallery || {};
  const settings = sections.settings || {};

  const hasHero = hero.title || hero.subtitle;
  const hasAbout = about.title || about.content;
  const hasFeatures = features.items?.length > 0;
  const hasTestimonials = testimonials.items?.length > 0;
  const hasVideos = videos.items?.length > 0;
  const hasGallery = gallery.items?.length > 0 || gallery.groups?.length > 0;
  const hasStats = Object.values(stats).some(v => v > 0);

  const logoUrl = settings.logo_url || 'https://customer-assets.emergentagent.com/job_anwar-reading/artifacts/9ouhjo98_%D8%A7%D9%84%D8%B4%D8%B9%D8%A7%D8%B1%20%D9%81%D9%82%D8%B7.svg';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf6f1]">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{borderColor: themeColors.primary}}></div>
    </div>
  );

  const pc = themeColors.primary;
  const ac = themeColors.accent;

  // Section order and colors from API
  const sectionOrder = sections._order || ['about', 'features', 'testimonials', 'videos', 'gallery'];
  const sectionColors = sections._colors || {};

  // Gallery: support groups or flat items
  const galleryGroups = gallery.groups || (gallery.items?.length > 0 ? [{ title: '', items: gallery.items }] : []);

  // Dynamic section renderers
  const renderSection = (key) => {
    const bg = sectionColors[key] || '';
    const bgStyle = bg ? { backgroundColor: bg } : {};

    switch (key) {
      case 'about':
        if (!hasAbout) return null;
        return (
          <FadeInSection key={key}>
            <section style={bgStyle} className="py-16 sm:py-20" data-testid="about-section">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {about.title && <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8" style={{color: ac}}>{about.title}</h2>}
                {about.content && <div className="prose prose-lg max-w-none text-right leading-relaxed" style={{color: `${ac}cc`}} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(about.content) }} />}
              </div>
            </section>
          </FadeInSection>
        );

      case 'features':
        if (!hasFeatures) return null;
        return (
          <FadeInSection key={key}>
            <section style={bgStyle} className={`py-16 sm:py-20 ${!bg ? 'bg-white' : ''}`} data-testid="features-section">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <SmartSlider slow>
                  {features.items.map((feat, i) => {
                    const IconComp = ICON_MAP[feat.icon] || Award;
                    const align = feat.align || 'right';
                    const textAlign = align === 'center' ? 'text-center' : align === 'left' ? 'text-left' : 'text-right';
                    const iconAlign = align === 'center' ? 'mx-auto' : align === 'left' ? 'ml-auto' : '';
                    return (
                      <div key={i} className={`bg-[#faf6f1] p-6 rounded-2xl hover:shadow-lg transition-shadow h-full ${textAlign}`} style={{border: `1px solid ${pc}15`}}>
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${iconAlign}`} style={{backgroundColor: `${pc}15`}}>
                          <IconComp className="w-6 h-6" style={{color: pc}} />
                        </div>
                        <h3 className="text-lg font-bold mb-2" style={{color: ac}}>{feat.title}</h3>
                        <p className="text-sm leading-relaxed" style={{color: `${ac}99`}}>{feat.description}</p>
                      </div>
                    );
                  })}
                </SmartSlider>
              </div>
            </section>
          </FadeInSection>
        );

      case 'testimonials':
        if (!hasTestimonials) return null;
        return (
          <FadeInSection key={key}>
            <section style={bgStyle} className="py-16 sm:py-20" data-testid="testimonials-section">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <TestimonialShowcase items={testimonials.items} pc={pc} ac={ac} />
              </div>
            </section>
          </FadeInSection>
        );

      case 'videos':
        if (!hasVideos) return null;
        return (
          <FadeInSection key={key}>
            <section style={bgStyle} className="py-16 sm:py-20" data-testid="videos-section">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
                {videos.items.map((vid, i) => {
                  const ytId = extractYouTubeId(vid.url);
                  if (!ytId) return null;
                  return (
                    <div key={i}>
                      {vid.title && <h3 className="text-2xl font-bold text-center mb-6" style={{color: ac}}>{vid.title}</h3>}
                      <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: `0 25px 50px -12px ${ac}35` }}>
                        <div className="absolute top-0 left-0 right-0 h-6 z-10 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${ac}20, transparent)` }} />
                        <div className="relative w-full" style={{paddingBottom: '56.25%'}}>
                          <iframe
                            src={`https://www.youtube.com/embed/${ytId}`}
                            title={vid.title || `فيديو ${i + 1}`}
                            className="absolute inset-0 w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-6 z-10 pointer-events-none" style={{ background: `linear-gradient(to top, ${ac}20, transparent)` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </FadeInSection>
        );

      case 'gallery':
        if (!hasGallery) return null;
        const captionSettings = gallery.captionSettings || { fontSize: 12, align: 'center' };
        return (
          <FadeInSection key={key}>
            <section style={bgStyle} className="py-16 sm:py-20" data-testid="gallery-section">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
                {galleryGroups.map((group, gi) => (
                  <div key={gi}>
                    {group.title && <h3 className="text-xl font-bold mb-4" style={{color: ac}}>{group.title}</h3>}
                    <SmartSlider>
                      {(group.items || []).map((img, i) => (
                        <div key={i} className="rounded-2xl overflow-hidden shadow-md h-full">
                          <img src={img.url} alt={img.caption || ''} className="w-full h-48 object-cover bg-gray-100" loading="lazy"
                            onError={(e) => { e.target.onerror = null; e.target.style.opacity = '0.3'; e.target.src = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="350" height="192"><rect fill="#f3f4f6" width="350" height="192"/><text x="50%" y="50%" text-anchor="middle" fill="#9ca3af" font-size="14">لا يمكن تحميل الصورة</text></svg>')}`; }} />
                          {img.caption && <p className="p-2" style={{color: `${ac}99`, fontSize: `${captionSettings.fontSize}px`, textAlign: captionSettings.align}}>{img.caption}</p>}
                        </div>
                      ))}
                    </SmartSlider>
                    {gi < galleryGroups.length - 1 && <div className="mt-8 border-b" style={{borderColor: `${pc}20`}} />}
                  </div>
                ))}
              </div>
            </section>
          </FadeInSection>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#faf6f1]" dir="rtl" data-testid="landing-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b shadow-sm" style={{borderColor: `${pc}15`}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="الشعار" className="h-12 w-auto" />
            {hero.title && <span className="hidden sm:block font-bold text-lg" style={{color: ac}}>{hero.title}</span>}
          </div>
          <div className="flex items-center gap-2 sm:gap-3" data-testid="landing-nav-actions">
            {siteSettings.show_login_button && (
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-1.5 px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-xl font-bold text-sm bg-white transition-all hover:scale-105 hover:shadow-md"
                style={{color: ac, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05)', border: `1px solid ${pc}25`}}
                data-testid="landing-login-btn"
              >
                تسجيل الدخول
              </button>
            )}
            {siteSettings.show_register_button && (
              <button
                onClick={() => navigate('/register')}
                className="relative group flex items-center gap-1.5 px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-xl font-bold text-white text-sm shadow-lg transition-all hover:scale-105 hover:shadow-xl overflow-hidden"
                style={{background: `linear-gradient(135deg, ${pc}, ${ac})`}}
                data-testid="landing-register-btn"
              >
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{background: `linear-gradient(135deg, ${ac}, ${pc})`}} />
                <span className="absolute -inset-px rounded-xl opacity-50 blur-sm" style={{background: `linear-gradient(135deg, ${pc}, ${ac})`}} />
                <UserPlus className="w-4 h-4 relative z-10" />
                <span className="relative z-10">التسجيل في المبادرة</span>
                <span className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-xl">
                  <span className="absolute -inset-y-1 -left-1/3 w-1/3 rotate-12 bg-white/30 blur-sm animate-[shine_3s_ease-in-out_infinite]" />
                </span>
                <style>{`@keyframes shine { 0%,100% { transform: translateX(-100%) rotate(12deg); } 50% { transform: translateX(400%) rotate(12deg); } }`}</style>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero - always first */}
      {hasHero && (
        <section className="relative overflow-hidden" data-testid="hero-section">
          {hero.bg_image ? (
            <div className="absolute inset-0 will-change-transform" style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.15)` }}>
              <img src={hero.bg_image} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{background: `linear-gradient(to top, ${ac}e6, ${ac}99, ${ac}66)`}} />
            </div>
          ) : (
            <div className="absolute inset-0 will-change-transform" style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.15)`, background: `linear-gradient(to bottom left, ${ac}, ${ac}cc, ${pc})` }} />
          )}
          <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30Z\' fill=\'none\' stroke=\'%23fff\' stroke-width=\'.5\'/%3E%3C/svg%3E")'}} />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title-display">{hero.title}</h1>
            {hero.subtitle && <p className="text-lg sm:text-xl text-white/90 mb-4 max-w-2xl mx-auto">{hero.subtitle}</p>}
            {hero.description && <p className="text-base text-white/75 max-w-xl mx-auto leading-relaxed">{hero.description}</p>}
            <div className="mt-10">
              {siteSettings.show_login_button && (
                <button onClick={() => navigate('/login')} className="bg-white hover:bg-white/90 px-8 py-3.5 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-2xl" style={{color: ac}}>
                  تسجيل الدخول <ChevronLeft className="w-5 h-5 inline-block mr-1" />
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Stats - always after hero */}
      {hasStats && (
        <FadeInSection>
          <section className="relative -mt-10 z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="stats-section">
            <div className="bg-white rounded-2xl shadow-xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
              {Object.entries(STAT_LABELS).map(([key, label]) => {
                const Icon = STAT_ICONS[key];
                const val = stats[key] || 0;
                return (
                  <div key={key} className="text-center">
                    <Icon className="w-8 h-8 mx-auto mb-2" style={{color: pc}} />
                    <p className="text-3xl font-bold" style={{color: ac}}><CountUp target={val} /></p>
                    <p className="text-sm mt-1" style={{color: `${ac}99`}}>{label}</p>
                  </div>
                );
              })}
            </div>
          </section>
        </FadeInSection>
      )}

      {/* Dynamic sections in admin-defined order */}
      {sectionOrder.map(key => renderSection(key))}

      {/* Footer - always last */}
      <footer style={{background: `linear-gradient(to bottom, ${ac}, ${ac}dd)`}} className="text-white py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={logoUrl} alt="الشعار" className="h-16 mx-auto mb-4 brightness-0 invert" />
          {hero.title && <p className="text-lg font-semibold mb-2">{hero.title}</p>}
          {settings.footer_text && <p className="text-white/60 text-sm">{settings.footer_text}</p>}
          <p className="text-white/50 text-sm mt-4">جميع الحقوق محفوظة 2023</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
